import React, { useState, useEffect } from 'react'
import { Clock } from 'lucide-react'

interface CountdownTimerProps {
  targetDate: Date
  label?: string
  onComplete?: () => void
  className?: string
}

interface TimeRemaining {
  days: number
  hours: number
  minutes: number
  seconds: number
  total: number
}

const CountdownTimer: React.FC<CountdownTimerProps> = ({
  targetDate,
  label = 'Time remaining',
  onComplete,
  className = ''
}) => {
  const [timeRemaining, setTimeRemaining] = useState<TimeRemaining>(
    calculateTimeRemaining(targetDate)
  )

  useEffect(() => {
    const timer = setInterval(() => {
      const remaining = calculateTimeRemaining(targetDate)
      setTimeRemaining(remaining)

      if (remaining.total <= 0 && onComplete) {
        onComplete()
        clearInterval(timer)
      }
    }, 1000)

    return () => clearInterval(timer)
  }, [targetDate, onComplete])

  if (timeRemaining.total <= 0) {
    return null
  }

  return (
    <div className={`flex items-center space-x-2 ${className}`}>
      <Clock className="w-5 h-5 text-white" />
      <div className="flex items-baseline space-x-2 text-white">
        <span className="font-medium text-white">{label}:</span>
        <span className="font-bold text-lg text-white">
          {formatTimeRemaining(timeRemaining)}
        </span>
      </div>
    </div>
  )
}

function calculateTimeRemaining(targetDate: Date): TimeRemaining {
  const now = new Date().getTime()
  const target = new Date(targetDate).getTime()
  const total = target - now

  if (total <= 0) {
    return { days: 0, hours: 0, minutes: 0, seconds: 0, total: 0 }
  }

  const seconds = Math.floor((total / 1000) % 60)
  const minutes = Math.floor((total / 1000 / 60) % 60)
  const hours = Math.floor((total / (1000 * 60 * 60)) % 24)
  const days = Math.floor(total / (1000 * 60 * 60 * 24))

  return { days, hours, minutes, seconds, total }
}

function formatTimeRemaining(time: TimeRemaining): string {
  const parts: string[] = []

  if (time.days > 0) {
    parts.push(`${time.days}d`)
  }
  if (time.hours > 0 || time.days > 0) {
    parts.push(`${time.hours}h`)
  }
  if (time.minutes > 0 || time.hours > 0 || time.days > 0) {
    parts.push(`${time.minutes}m`)
  }
  if (time.days === 0) {
    parts.push(`${time.seconds}s`)
  }

  return parts.join(' ')
}

export default CountdownTimer
